import React, { useState, memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Card, Typography, Tooltip, Chip, IconButton, Box } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useWorkflow } from '../../context/WorkflowContext';

interface AgentNodeBoxProps {
  data: {
    label: string;
    agent: any;
    type?: string;
    isDragging: boolean;
    onDelete: (id: string) => void;
    onChangeLabel: (id: string, label: string) => void;
    onUpdate: (node: any) => void;
  };
  id: string;
  selected?: boolean;
}

const AgentNodeBox: React.FC<AgentNodeBoxProps> = memo(({ data, id, selected }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [labelValue, setLabelValue] = useState(data.label || '');
  const { setHasUnsavedChanges } = useWorkflow();

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabelValue(e.target.value);
    // Update the agent ID when label changes
    if (data.agent) {
      data.agent.id = e.target.value;
    }
  };

  const handleLabelBlur = () => {
    setIsEditing(false);
    if (data.onChangeLabel) {
      data.onChangeLabel(id, labelValue);
      setHasUnsavedChanges(true);
      // Also trigger an update to sync the agent ID
      if (data.onUpdate && data.agent) {
        data.agent.id = labelValue;
        data.onUpdate({
          id: labelValue,
          data: {
            ...data,
            label: labelValue,
            agent: {
              ...data.agent,
              id: labelValue
            }
          }
        });
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleLabelBlur();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (data.onDelete) {
      data.onDelete(id);
      setHasUnsavedChanges(true);
    }
  };

  const getNodeTypeColor = (type: string = 'agent') => {
    switch (type) {
      case 'workflow':
        return 'blue';
      case 'loop':
        return 'orange';
      case 'input_node':
        return 'green';
      case 'input_agent':
        return 'cyan';
      default:
        return 'purple';
    }
  };

  const getNodeMetrics = () => {
    if (data.type === 'workflow') {
      return `Sub-workflow: ${data.agent?.workflowId || 'Not set'}`;
    }
    if (data.type === 'input_node') {
      return `${data.agent?.requiredParameters?.length || 0} parameters`;
    }
    if (data.type === 'input_agent') {
      return `Input Agent: ${data.agent?.proceduralMemory?.steps?.length || 0} steps`;
    }
    return `${data.agent?.proceduralMemory?.steps?.length || 0} steps`;
  };

  const getNodeBackground = (type: string = 'agent') => {
    switch (type) {
      case 'workflow':
        return 'linear-gradient(135deg, #ffffff, #e3f2fd)';
      case 'loop':
        return 'linear-gradient(135deg, #ffffff, #fff3e0)';
      case 'input_node':
        return 'linear-gradient(135deg, #ffffff, #e8f5e9)';
      case 'input_agent':
        return 'linear-gradient(135deg, #ffffff, #e0f7fa)';
      default:
        return 'linear-gradient(135deg, #ffffff, #fce4ec)';
    }
  };

  const getBorderColor = (type: string = 'agent', isSelected: boolean = false) => {
    if (isSelected) return '#ce00b3';
    
    switch (type) {
      case 'workflow':
        return '#2196f3';
      case 'loop':
        return '#ff9800';
      case 'input_node':
        return '#4caf50';
      case 'input_agent':
        return '#00bcd4';
      default:
        return '#ce00b3';
    }
  };

  return (
    <>
      <Card
        className={`node-card ${selected ? 'selected' : ''} ${data.isDragging ? 'dragging' : ''}`}
        style={{
          background: getNodeBackground(data.type),
          borderColor: getBorderColor(data.type, selected),
          borderWidth: selected ? '3px' : '2px',
          boxShadow: data.isDragging 
            ? '0 12px 24px rgba(0,0,0,0.2)' 
            : selected 
              ? '0 0 0 3px rgba(206,0,179,0.2), 0 4px 12px rgba(0,0,0,0.1)' 
              : '0 4px 12px rgba(0,0,0,0.1)'
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          className="w-4 h-4 !bg-pink-500 border-2 border-white"
          style={{ 
            left: '-0.5rem', 
            top: '50%',
            boxShadow: '0 0 0 3px rgba(206,0,179,0.15), 0 2px 5px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
        
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            {isEditing ? (
              <input
                type="text"
                value={labelValue}
                onChange={handleLabelChange}
                onBlur={handleLabelBlur}
                onKeyPress={handleKeyPress}
                autoFocus
                className="node-label-input"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="flex flex-col">
                <Tooltip title={labelValue}>
                  <Typography
                    variant="subtitle1"
                    className="cursor-pointer font-medium text-base"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                    noWrap
                  >
                    {data.type || 'agent'} Node
                  </Typography>
                </Tooltip>
                <Typography variant="caption" className="text-xs text-gray-500" noWrap title={data.agent?.id || id}>
                  ID: {data.agent?.id || id}
                </Typography>
              </div>
            )}
            <Box className="node-actions flex gap-2">
              <IconButton
                size="small"
                className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={handleDelete}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </div>

          <Box className="flex items-center justify-between">
            <Chip 
              label={data.type || 'agent'} 
              color={getNodeTypeColor(data.type) as any} 
              size="small"
              className="px-2 py-1"
            />
            <Typography variant="body2" className="text-sm text-gray-500">
              {getNodeMetrics()}
            </Typography>
          </Box>
        </div>

        <Handle
          type="source"
          position={Position.Right}
          className="w-4 h-4 !bg-pink-500 border-2 border-white"
          style={{ 
            right: '-0.5rem', 
            top: '50%',
            boxShadow: '0 0 0 3px rgba(206,0,179,0.15), 0 2px 5px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
      </Card>
    </>
  );
});

AgentNodeBox.displayName = 'AgentNodeBox';

export default AgentNodeBox;